import React, { useState, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import { Modal, Button, ListGroup, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import parsePhoneNumberFromString from "libphonenumber-js";
import { workflowApi } from "../../Apis/Api";
import { apiCall } from "../../Apis/apiCall";
import CustomPhoneInput from "../../components/common/CustomPhoneInput";

const CustomerPhoneNumber = ({
  name,
  control,
  errors,
  defaultCountry = "sa",
  label = "Phone Number",
  labelName = false,
  placeholder = "Enter phone number",
  onChange,
  formData,
  setFormData,
  getFormFields,
  cleanCustomerData,
  setFetchingCustomerData
}) => {
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const previousValidValue = useRef(null);
  const [countries, setCountries] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const isFetching = useRef(false); // For loop prevention

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiCall(
          "get",
          "/get-all-countries?want_pagination=0"
        );
        if (response.data.success) {
          setCountries(response.data.data);
        } else {
          // toast.error("Failed to fetch countries");
        }
      } catch (err) {
        // toast.error("Failed to fetch countries");
      }
    };
    fetchCountries();
  }, []);

  // Validate phone number
  const validatePhoneNumber = (value) => {
    try {
      const parsed = parsePhoneNumberFromString(
        value,
        defaultCountry.toUpperCase()
      );
      return parsed?.isValid() || false;
    } catch (err) {
      return false;
    }
  };

  // Fetch customer data by phone number
  const fetchCustomerData = async (phoneNumber) => {
    if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
      return [];
    }

    setLoading(true);
    setFetchingCustomerData(true);
    try {
      // console.log("📞 Making API call...");
      const response = await workflowApi.getCustomerDataByPhoneNumber(
        phoneNumber
      );
      // console.log("📞 API Response:", response);
      return response.data || [];
    } catch (error) {
      // console.error("📞 API Error:", error);
      // toast.error("Failed to fetch customer data");
      setLoading(false);
      setFetchingCustomerData(false);
      return [];
    }  // finally {
    //   setLoading(false);
    //   setFetchingCustomerData(false);
    // }
  };

  // Handle phone number change
  const handlePhoneChange = async (value) => {
    const isValid = validatePhoneNumber(value);

    // console.log("📞 PHONE INPUT - Value:", value, "Valid:", isValid);

    if (!isValid && previousValidValue.current) {
      // console.log("🧹 Cleaning customer data - number became invalid");
      // cleanCustomerData();
      previousValidValue.current = null;
      setCustomerOptions([]);
      setSelectedCustomer(null);
    }

    if (onChange) {
      onChange(value || "");
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value || "",
    }));

    if (isValid) {
      previousValidValue.current = value;
      if (isFetching.current) {
        // console.log("Skipping fetch to prevent loop");
        return;
      }
      isFetching.current = true;
      const customers = await fetchCustomerData(value);
      isFetching.current = false;

      if (customers.length > 0) {
        // if (customers.length === 1) {
        //   await handleCustomerSelect(customers[0]);
        // } else {
        //   setCustomerOptions(customers);
        //   setShowCustomerModal(true);
        // }
        setCustomerOptions(customers);
        setShowCustomerModal(true);
      } else {
        // console.log("📞 No customer data found");
        // cleanCustomerData();
        setSelectedCustomer(null);
        // toast.info("No customer data found for this phone number");
      }
    } else if (value === "" || value === null) {
      // console.log("🧹 Cleaning customer data - field emptied");
      // cleanCustomerData();
      previousValidValue.current = null;
      setCustomerOptions([]);
      setSelectedCustomer(null);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = async (customer) => {
    // console.log("📞 CUSTOMER SELECTED:", customer);

    setSelectedCustomer(customer);
    setShowCustomerModal(false);

    const currentFormFields = getFormFields();
    // console.log(
    //   "📞 CURRENT FORM FIELDS:",
    //   currentFormFields.map((f) => ({
    //     name: f.name,
    //     label: f.label,
    //   }))
    // );

    const updates = {};

    const possibleMappings = [
      { apiField: "customer_name", formField: "customer_name" },
      { apiField: "email", formField: "customer_email" },
      { apiField: "customer_id", formField: "customer_id" },
      { apiField: "mobile_number", formField: "mobile_number" },
      { apiField: "national_id", formField: "national_id" },
      { apiField: "vehicle_color", formField: "vehicle_color" },
      { apiField: "make_id", formField: "vehicle_make" },
      { apiField: "model_id", formField: "vehicle_model" },
      { apiField: "type_id", formField: "vehicle_type" },
      { apiField: "make_name", formField: "make_name" },
      { apiField: "model_name", formField: "model_name" },
      { apiField: "type_name", formField: "type_name" },
      { apiField: "plate_number", formField: "plate_number" },
      { apiField: "country_id", formField: "plate_country_id" },
      { apiField: "authority_id", formField: "plate_authority_id" },
      { apiField: "country_name", formField: "registration_country" },
    ];

    possibleMappings.forEach((mapping) => {
      const fieldExists = currentFormFields.some(
        (f) => f.name === mapping.formField
      );
      let value = customer[mapping.apiField];
      const hasData = value !== null && value !== undefined && value !== "";

      if (hasData) {
        if (mapping.formField === "plate_number") {
          value = String(value).replace(/\s/g, ""); // Normalize plate number
        } else if (
          ["plate_country_id", "plate_authority_id"].includes(mapping.formField)
        ) {
          value = Number(value); // Ensure IDs are numbers
        }
        if (
          fieldExists ||
          ["plate_number", "plate_country_id", "plate_authority_id"].includes(
            mapping.formField
          )
        ) {
          updates[mapping.formField] = value;
        }
      }
    });

    // Fetch authorities for country_id if needed
    if (updates.plate_country_id) {
      try {
        const response = await apiCall(
          "get",
          `/get-plate-authorities-by-country-id/${updates.plate_country_id}?want_pagination=0`
        );
        if (response.data.success) {
          setAuthorities(response.data.data);
          // console.log(
          //   "📞 Authorities fetched for country_id:",
          //   updates.plate_country_id
          // );
        } else {
          // toast.error("Failed to fetch authorities");
        }
      } catch (err) {
        // toast.error("Failed to fetch authorities for country");
      }
    }

    // console.log("📞 UPDATES TO APPLY:", updates);

    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({
        ...prev,
        ...updates,
      }));
      toast.success("Customer data loaded successfully!");
      setFetchingCustomerData(false);
      setLoading(false);
    } else {
      // console.log("📞 No matching fields found to update");
      // toast.info("Customer data found but no matching fields in current form");
    }
  };

  // Clear selected customer
  const handleClearSelection = () => {
    setSelectedCustomer(null);
    cleanCustomerData();
    toast.info("Customer selection cleared");
  };

  // Customer info display component
  const CustomerInfoBadge = () => {
    if (!selectedCustomer) return null;

    return (
      <div className="mt-2 p-2 border rounded bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong>Selected Customer:</strong>{" "}
            {selectedCustomer.customer_name || "N/A"}
            {selectedCustomer.vehicle_make && (
              <Badge bg="secondary" className="ms-2">
                {selectedCustomer.vehicle_make}
                {selectedCustomer.vehicle_model &&
                  ` ${selectedCustomer.vehicle_model}`}
              </Badge>
            )}
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleClearSelection}
          >
            ✕
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
.customer-modal-card {
    background: var(--white);
    border-radius: var(--rounded-12);
    padding: var(--size-16);
    margin-bottom: var(--size-8);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1.5px solid var(--border-color);
    cursor: pointer;
    font-family: var(--default-font);
    position: relative;
    overflow: hidden;
}

.customer-modal-card::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: linear-gradient(135deg, var(--brand) 0%, var(--primary-700) 100%);
    transform: scaleY(0);
    transition: transform 0.25s ease;
}

.customer-modal-card:hover {
    border-color: var(--brand);
    box-shadow: var(--shadow-5);
    transform: translateY(-2px);
}

.customer-modal-card:hover::before {
    transform: scaleY(1);
}

.customer-modal-card .customer-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--size-10);
    gap: var(--size-12);
}

.customer-modal-card .customer-name {
    font-size: var(--font-md);
    font-weight: 700;
    color: var(--text-primary-light);
    line-height: 1.3;
    flex: 1;
}

.customer-modal-card .model-badge {
    font-size: var(--font-xxs);
    color: var(--text-secondary-light);
    background: var(--primary-50);
    padding: 3px 8px;
    border-radius: var(--rounded-12);
    font-weight: 600;
    white-space: nowrap;
}

.customer-modal-card .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--size-8);
    margin-bottom: var(--size-8);
}

.customer-modal-card .info-item {
    display: flex;
    align-items: center;
    gap: var(--size-6);
    color: var(--text-secondary-workflow);
    font-size: var(--font-xs);
}

.customer-modal-card .info-item i {
    width: 16px;
    text-align: center;
    color: var(--brand);
    font-size: var(--font-sm);
    flex-shrink: 0;
}

.customer-modal-card .info-item span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.customer-modal-card .badges-row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--size-6);
    align-items: center;
    margin-top: var(--size-10);
}

.customer-modal-card .mini-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: var(--rounded-16);
    font-size: var(--font-xxs);
    font-weight: 600;
    white-space: nowrap;
}

.customer-modal-card .loyalty-badge {
    background: linear-gradient(135deg, var(--purple) 0%, var(--indigo) 100%);
    color: var(--base);
}

.customer-modal-card .country-badge {
    background: var(--info-50);
    color: var(--info-main);
    border: 1px solid var(--info-border);
}

.customer-modal-card .blacklisted-badge {
    background: var(--danger-50);
    color: var(--danger-main);
    border: 1px solid var(--danger-border);
}

.customer-modal-card .last-seen {
    font-size: var(--font-xxs);
    color: var(--text-secondary-light);
    margin-top: var(--size-8);
    text-align: right;
    font-style: italic;
}

.modal-body .list-group-item {
    border: none;
    padding: var(--size-4) 0;
    background: transparent;
}

.modal-body .list-group-item:hover {
    background-color: transparent;
}

.modal-content {
    background: var(--base);
    border: 1px solid var(--border-color);
    border-radius: var(--rounded-16);
}

.modal-header {
    border-bottom: 1px solid var(--border-color);
    background: var(--neutral-50);
    padding: var(--size-16) var(--size-20);
}

.modal-title {
    color: var(--text-primary-light);
    font-family: var(--default-font);
    font-weight: 700;
    font-size: var(--font-xl);
}

.modal-body {
    background: var(--bg-color);
    padding: var(--size-16) var(--size-20);
    max-height: 70vh;
    overflow-y: auto;
}

.modal-body > p {
    color: var(--text-secondary-workflow);
    font-size: var(--font-sm);
}

.modal-footer {
    border-top: 1px solid var(--border-color);
    background: var(--neutral-50);
    padding: var(--size-12) var(--size-20);
}

/* Custom scrollbar */
.modal-body::-webkit-scrollbar {
    width: 6px;
}

.modal-body::-webkit-scrollbar-track {
    background: var(--neutral-100);
    border-radius: 10px;
}

.modal-body::-webkit-scrollbar-thumb {
    background: var(--neutral-400);
    border-radius: 10px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-500);
}

@media (max-width: 768px) {
    .customer-modal-card .info-grid {
        grid-template-columns: 1fr;
        gap: var(--size-6);
    }
    
    .customer-modal-card .customer-header {
        flex-direction: column;
        gap: var(--size-6);
    }
    
    .customer-modal-card .model-badge {
        align-self: flex-start;
    }
}

@media (max-width: 576px) {
    .customer-modal-card {
        padding: var(--size-12);
    }
    
    .modal-header, .modal-body, .modal-footer {
        padding: var(--size-12) var(--size-16);
    }
    
    .customer-modal-card .customer-name {
        font-size: var(--font-sm);
    }
    
    .customer-modal-card .info-item {
        font-size: 11px;
    }
}
`}
      </style>
      <div className="row">
        <div className="col-12">
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <CustomPhoneInput
                {...field}
                name={name}
                control={control}
                errors={errors}
                defaultCountry={defaultCountry}
                label={label}
                labelName={labelName}
                placeholder={placeholder}
                onChange={(value) => {
                  field.onChange(value);
                  handlePhoneChange(value);
                }}
              />
            )}
          />

          {loading && (
            <div className="mt-1">
              <small className="">
                <span className="spinner-border spinner-border-sm me-1"></span>
                Searching customers...
              </small>
            </div>
          )}

          {/* <CustomerInfoBadge /> */}

          <Modal
            show={showCustomerModal}
            // onHide={() => setShowCustomerModal(false)}
            onHide={() => {
              setShowCustomerModal(false);
              setFetchingCustomerData(false);   // ← YEH LINE ADD KARO!
              setLoading(false);
            }}
            centered
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>Select Customer</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-3">
                Multiple customers found. Select one to continue:
              </p>

              <ListGroup variant="flush">
                {customerOptions.map((customer, index) => (
                  <ListGroup.Item
                    key={index}
                    action
                    onClick={() => handleCustomerSelect(customer)}
                  >
                    <div className="customer-modal-card">
                      <div className="customer-header">
                        <div className="customer-name">
                          {customer.customer_name || "Unknown Customer"}
                        </div>
                        {/* {customer.model_name && (
                          <div className="model-badge">
                            {customer.model_name}
                          </div>
                        )} */}
                      </div>

                      <div className="info-grid">
                        {customer.make_name && (
                          <div className="info-item">
                            <i className="bi bi-car-front-fill"></i>
                            <span>
                              {`${customer.model_name &&
                                `${customer.model_name} -`
                                } ${customer.make_name && `${customer.make_name} -`
                                } ${customer.vehicle_color && customer.vehicle_color
                                }`}
                            </span>
                          </div>
                        )}

                        {customer.type_name && (
                          <div className="info-item">
                            <i className="bi bi-tag-fill"></i>
                            <span>{customer.type_name}</span>
                          </div>
                        )}
                        {customer.plate_number && (
                          <div className="info-item">
                            <i className="bi bi-credit-card-2-front"></i>
                            <span>{customer.plate_number}</span>
                          </div>
                        )}

                        {customer.email && (
                          <div className="info-item">
                            <i className="bi bi-envelope-fill"></i>
                            <span>{customer.email}</span>
                          </div>
                        )}

                        {customer.mobile_number && (
                          <div className="info-item">
                            <i className="bi bi-telephone-fill"></i>
                            <span>{customer.mobile_number}</span>
                          </div>
                        )}

                        {customer.national_id && (
                          <div className="info-item">
                            <i className="bi bi-person-badge-fill"></i>
                            <span>{customer.national_id}</span>
                          </div>
                        )}
                      </div>

                      <div className="badges-row">
                        {/* {customer.loyalty_code && (
                          <span className="mini-badge loyalty-badge">
                            <i className="bi bi-star-fill"></i>
                            {customer.loyalty_code}
                          </span>
                        )} */}

                        {customer.country_name && (
                          <span className="mini-badge country-badge">
                            <i className="bi bi-globe"></i>
                            {`${customer.country_name &&
                              `${customer.country_name}`
                              }`}{" "}
                            -{" "}
                            {`${customer.authority_name &&
                              `${customer.authority_name}`
                              }`}
                          </span>
                        )}

                        {customer.is_blacklisted && (
                          <span className="mini-badge blacklisted-badge">
                            <i className="bi bi-exclamation-triangle-fill"></i>
                            Blacklisted
                          </span>
                        )}
                      </div>

                      {customer.last_seen && (
                        <div className="last-seen">
                          Last seen: {formatDate(customer.last_seen)}
                        </div>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                // onClick={() => setShowCustomerModal(false)}
                onClick={() => {
                  setShowCustomerModal(false);
                  setFetchingCustomerData(false);   // ← Yahan bhi false
                  setLoading(false);
                }}
              >
                Cancel
              </Button>
              {/* <Button
                variant="outline-secondary"
                onClick={() => {
                  cleanCustomerData();
                  setShowCustomerModal(false);
                  toast.info("No customer data applied");
                }}
              >
                Use None
              </Button> */}
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default CustomerPhoneNumber;
